import { CharStream, CommonTokenStream, ErrorListener } from "antlr4";
// @ts-ignore
import FormulaLexer from "@/lib/formula/FormulaLexer";
// @ts-ignore
import FormulaParser from "@/lib/formula/FormulaParser";

import { FormulaDiagnostic, FormulaFormatMode } from "./formulaEditor.types";

const DEFAULT_TOKEN_CHANNEL = 0;

interface GetTokensOptions {
	includeHiddenTokens?: boolean;
}

class SyntaxErrorListener extends ErrorListener<any> {
	private errors: FormulaDiagnostic[] = [];

	public syntaxError(
		recognizer: any,
		offendingSymbol: any,
		line: number,
		column: number,
		msg: string,
		e: any
	): void {
		this.errors.push({
			startLineNumber: line,
			startColumn: column + 1,
			endLineNumber: line,
			endColumn: column + 1 + (offendingSymbol ? offendingSymbol.stop - offendingSymbol.start + 1 : 0),
			message: msg,
			severity: "error"
		});
	}

	public getErrors(): FormulaDiagnostic[] {
		return this.errors;
	}
}

export function createLexer(text: string): FormulaLexer {
	return new FormulaLexer(new CharStream(text));
}

export function createTokenStream(lexer: FormulaLexer): CommonTokenStream {
	const tokenStream = new CommonTokenStream(lexer);
	tokenStream.fill();
	return tokenStream;
}

export function createParserContext(text: string): {
	lexer: FormulaLexer;
	tokenStream: CommonTokenStream;
	parser: FormulaParser;
} {
	const lexer = createLexer(text);
	const tokenStream = createTokenStream(lexer);
	const parser = new FormulaParser(tokenStream);

	return {
		lexer,
		tokenStream,
		parser
	};
}

function isLineCommentToken(token?: any): boolean {
	return token?.type === FormulaLexer.LINE_COMMENT;
}

function getPreviousNonCommentToken(tokens: any[], startIndex: number): any | undefined {
	for (let index = startIndex - 1; index >= 0; index--) {
		if (!isLineCommentToken(tokens[index])) {
			return tokens[index];
		}
	}

	return undefined;
}

function getPreviousNonCommentTokenIndex(tokens: any[], startIndex: number): number {
	for (let index = startIndex - 1; index >= 0; index--) {
		if (!isLineCommentToken(tokens[index])) {
			return index;
		}
	}

	return -1;
}

function getNextNonCommentToken(tokens: any[], startIndex: number): any | undefined {
	for (let index = startIndex + 1; index < tokens.length; index++) {
		if (!isLineCommentToken(tokens[index])) {
			return tokens[index];
		}
	}

	return undefined;
}

function trimTrailingWhitespacePreservingIndentation(value: string): string {
	const lineStartIndex = value.lastIndexOf("\n") + 1;
	const currentLine = value.slice(lineStartIndex);

	if (!currentLine.trim().length) {
		return value;
	}

	return `${value.slice(0, lineStartIndex)}${currentLine.trimEnd()}`;
}

function appendLineComment(formatted: string, commentText: string): string {
	const trimmedComment = commentText.trimEnd();
	const currentLine = formatted.slice(formatted.lastIndexOf("\n") + 1);

	if (currentLine.trim().length) {
		return `${trimTrailingWhitespacePreservingIndentation(formatted)}  ${trimmedComment}`;
	}

	return `${formatted}${trimmedComment}`;
}

function isOnSameSourceLine(token?: any, otherToken?: any): boolean {
	return Boolean(token && otherToken && token.line === otherToken.line);
}

export function getTokens(text: string, options: GetTokensOptions = {}): any[] {
	return (createTokenStream(createLexer(text)).tokens || []).filter(token => {
		if (token.type === FormulaLexer.EOF) {
			return false;
		}

		return options.includeHiddenTokens || token.channel === DEFAULT_TOKEN_CHANNEL;
	});
}

export function collectSyntaxErrors(text: string): FormulaDiagnostic[] {
	if (!text.trim()) {
		return [];
	}

	const lexer = createLexer(text);
	const listener = new SyntaxErrorListener();

	lexer.removeErrorListeners();
	lexer.addErrorListener(listener);

	const tokenStream = createTokenStream(lexer);
	const parser = new FormulaParser(tokenStream);
	parser.removeErrorListeners();
	parser.addErrorListener(listener);

	parser.buildParseTrees = false;
	parser.calculable();

	return listener.getErrors();
}

function appendFormattedToken(formatted: string, token: any, previousToken?: any): string {
	const trimmed = trimTrailingWhitespacePreservingIndentation(formatted);

	switch (token.type) {
		case FormulaLexer.T__0:
			return `${trimmed}:`;
		case FormulaLexer.T__1:
			return `${trimmed}, `;
		case FormulaLexer.LPAREN:
			if (shouldTrimBeforeOpeningParenthesis(previousToken)) {
				return `${trimmed}(`;
			}
			return `${trimmed} (`;
		case FormulaLexer.RPAREN:
			return `${trimmed})`;
		case FormulaLexer.PLUS:
		case FormulaLexer.MINUS:
		case FormulaLexer.TIMES:
		case FormulaLexer.DIVIDE:
		case FormulaLexer.GT:
		case FormulaLexer.LT:
		case FormulaLexer.GTE:
		case FormulaLexer.LTE:
		case FormulaLexer.EQ:
		case FormulaLexer.NE:
		case FormulaLexer.AND:
		case FormulaLexer.OR:
			return `${trimmed} ${token.text} `;
		default:
			return `${formatted}${token.text}`;
	}
}

function shouldTrimBeforeOpeningParenthesis(previousToken?: any): boolean {
	return (
		!previousToken ||
		previousToken.type === FormulaLexer.IDENTIFIER ||
		previousToken.type === FormulaLexer.LPAREN ||
		previousToken.type === FormulaLexer.NOT
	);
}

function formatFormulaCompact(text: string): string {
	let formatted = "";
	const tokens = getTokens(text, { includeHiddenTokens: true });
	const indentUnit = "  ";
	let nestingDepth = 0;
	let shouldBreakAfterComment = false;

	const appendNewLine = (targetIndentLevel: number = nestingDepth) => {
		formatted = formatted.trimEnd();
		if (formatted.length) {
			formatted += "\n";
		}
		formatted += indentUnit.repeat(Math.max(0, targetIndentLevel));
	};

	for (let index = 0; index < tokens.length; index++) {
		const token = tokens[index];
		const previousToken = getPreviousNonCommentToken(tokens, index);

		if (isLineCommentToken(token)) {
			if (shouldBreakAfterComment) {
				appendNewLine();
			}
			formatted = appendLineComment(formatted, token.text);
			shouldBreakAfterComment = true;
			continue;
		}

		if (shouldBreakAfterComment) {
			appendNewLine(token.type === FormulaLexer.RPAREN ? Math.max(0, nestingDepth - 1) : nestingDepth);
			shouldBreakAfterComment = false;
		}

		formatted = appendFormattedToken(formatted, token, previousToken);

		if (token.type === FormulaLexer.LPAREN) {
			nestingDepth++;
		} else if (token.type === FormulaLexer.RPAREN) {
			nestingDepth = Math.max(0, nestingDepth - 1);
		}
	}

	return formatted.trim();
}

interface OpenParenthesisContext {
	commaCount: number;
	hasLogicalOperator: boolean;
	index: number;
	isFunctionCall: boolean;
	startIndex: number;
}

function getExpandedParenthesisIndices(tokens: any[]): Set<number> {
	const expandedParenthesisIndices = new Set<number>();
	const openParenthesisStack: OpenParenthesisContext[] = [];

	for (let index = 0; index < tokens.length; index++) {
		const token = tokens[index];
		const previousToken = getPreviousNonCommentToken(tokens, index);
		const previousTokenIndex = getPreviousNonCommentTokenIndex(tokens, index);

		if (isLineCommentToken(token)) {
			continue;
		}

		if (token.type === FormulaLexer.LPAREN) {
			const isFunctionCall = previousToken?.type === FormulaLexer.IDENTIFIER;

			openParenthesisStack.push({
				commaCount: 0,
				hasLogicalOperator: false,
				index,
				isFunctionCall,
				startIndex: isFunctionCall && previousTokenIndex >= 0 ? previousTokenIndex : index
			});
			continue;
		}

		if (token.type === FormulaLexer.RPAREN) {
			const context = openParenthesisStack.pop();
			if (!context) {
				continue;
			}

			const nextTokenAfterOpen = tokens[context.index + 1];
			const isEmptyParenthesis = nextTokenAfterOpen?.type === FormulaLexer.RPAREN;
			const argumentCount = context.isFunctionCall && !isEmptyParenthesis ? context.commaCount + 1 : 0;
			const contentLength = token.stop - tokens[context.startIndex].start + 1;
			const shouldExpand = context.isFunctionCall
				? context.hasLogicalOperator || argumentCount >= 3 || (argumentCount >= 2 && contentLength > 32)
				: context.hasLogicalOperator;

			if (shouldExpand && !isEmptyParenthesis) {
				expandedParenthesisIndices.add(context.index);
			}

			continue;
		}

		const currentContext = openParenthesisStack[openParenthesisStack.length - 1];
		if (!currentContext) {
			continue;
		}

		if (token.type === FormulaLexer.T__1) {
			currentContext.commaCount++;
			continue;
		}

		if (token.type === FormulaLexer.AND || token.type === FormulaLexer.OR) {
			currentContext.hasLogicalOperator = true;
		}
	}

	return expandedParenthesisIndices;
}

function formatFormulaReadable(text: string): string {
	const tokens = getTokens(text, { includeHiddenTokens: true });
	const expandedParenthesisIndices = getExpandedParenthesisIndices(tokens);
	const indentUnit = "  ";
	const parenthesisStack: Array<{
		breakAfterComma: boolean;
		breakAfterOpen: boolean;
		hasLineBreak: boolean;
		openingIndentLevel: number;
	}> = [];
	let formatted = "";
	let indentLevel = 0;

	const append = (value: string) => {
		formatted += value;
	};

	const appendTrimmed = (value: string) => {
		formatted = trimTrailingWhitespacePreservingIndentation(formatted);
		formatted += value;
	};

	const appendNewLine = (targetIndentLevel: number = indentLevel) => {
		formatted = formatted.trimEnd();
		if (formatted.length) {
			formatted += "\n";
		}
		formatted += indentUnit.repeat(Math.max(0, targetIndentLevel));
		for (const context of parenthesisStack) {
			context.hasLineBreak = true;
		}
	};

	const canAppendClosingParenthesisToCurrentLine = (targetIndentLevel: number) => {
		const currentLine = formatted.slice(formatted.lastIndexOf("\n") + 1);
		const indentation = indentUnit.repeat(Math.max(0, targetIndentLevel));

		if (!currentLine.startsWith(indentation)) {
			return false;
		}

		const remainder = currentLine.slice(indentation.length);
		return remainder.length > 0 && /^\)+$/.test(remainder);
	};
	let shouldBreakAfterComment = false;

	for (let index = 0; index < tokens.length; index++) {
		const token = tokens[index];
		const previousToken = getPreviousNonCommentToken(tokens, index);
		const nextToken = getNextNonCommentToken(tokens, index);
		const nextRawToken = tokens[index + 1];
		const shouldStartNewLine = shouldBreakAfterComment;

		if (!isLineCommentToken(token)) {
			shouldBreakAfterComment = false;
		}

		switch (token.type) {
			case FormulaLexer.LINE_COMMENT:
				if (shouldStartNewLine) {
					appendNewLine();
				}
				formatted = appendLineComment(formatted, token.text);
				shouldBreakAfterComment = true;
				break;
			case FormulaLexer.T__0:
				if (shouldStartNewLine) {
					appendNewLine();
				}
				appendTrimmed(":");
				break;
			case FormulaLexer.T__1:
				if (shouldStartNewLine) {
					appendNewLine();
				}
				appendTrimmed(",");
				if (parenthesisStack[parenthesisStack.length - 1]?.breakAfterComma) {
					if (!isLineCommentToken(nextRawToken) || !isOnSameSourceLine(token, nextRawToken)) {
						appendNewLine();
					}
				} else {
					append(" ");
				}
				break;
			case FormulaLexer.LPAREN: {
				const isFunctionCall = previousToken?.type === FormulaLexer.IDENTIFIER;
				const breakAfterOpen = Boolean(expandedParenthesisIndices.has(index) && nextToken?.type !== FormulaLexer.RPAREN);
				const breakAfterComma = Boolean(isFunctionCall && breakAfterOpen);
				if (shouldStartNewLine) {
					appendNewLine();
				}
				if (shouldTrimBeforeOpeningParenthesis(previousToken)) {
					appendTrimmed("(");
				} else {
					appendTrimmed(" (");
				}
				parenthesisStack.push({
					breakAfterComma,
					breakAfterOpen,
					hasLineBreak: false,
					openingIndentLevel: indentLevel
				});
				if (breakAfterOpen) {
					indentLevel++;
					if (!isLineCommentToken(nextRawToken) || !isOnSameSourceLine(token, nextRawToken)) {
						appendNewLine();
					}
				}
				break;
			}
			case FormulaLexer.RPAREN: {
				const parenthesisContext = parenthesisStack.pop();
				const shouldCloseOnNewLine = Boolean(
					shouldStartNewLine || parenthesisContext?.breakAfterOpen || parenthesisContext?.hasLineBreak
				);
				if (parenthesisContext?.breakAfterOpen) {
					indentLevel = Math.max(0, indentLevel - 1);
				}
				if (shouldCloseOnNewLine && parenthesisContext) {
					if (!canAppendClosingParenthesisToCurrentLine(parenthesisContext.openingIndentLevel)) {
						appendNewLine(parenthesisContext.openingIndentLevel);
					}
					append(")");
				} else {
					appendTrimmed(")");
				}
				break;
			}
			case FormulaLexer.AND:
			case FormulaLexer.OR:
				appendNewLine();
				append(`${token.text} `);
				break;
			case FormulaLexer.PLUS:
			case FormulaLexer.MINUS:
			case FormulaLexer.TIMES:
			case FormulaLexer.DIVIDE:
			case FormulaLexer.GT:
			case FormulaLexer.LT:
			case FormulaLexer.GTE:
			case FormulaLexer.LTE:
			case FormulaLexer.EQ:
			case FormulaLexer.NE:
				if (shouldStartNewLine) {
					appendNewLine();
				}
				appendTrimmed(` ${token.text} `);
				break;
			default:
				if (shouldStartNewLine) {
					appendNewLine();
				}
				append(token.text);
		}
	}

	return formatted.trim();
}

export function formatFormula(text: string, mode: FormulaFormatMode = "compact"): string {
	if (!text.trim()) {
		return text;
	}

	if (collectSyntaxErrors(text).length) {
		return text;
	}

	if (mode === "readable") {
		return formatFormulaReadable(text);
	}

	return formatFormulaCompact(text);
}
